
import { useState } from 'react';
import { Property } from '@/types/property';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { usePropertyMutations } from '@/hooks/usePropertyMutations';
import { Edit, Trash } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface AdminPropertyCardProps {
  property: Property;
  onEdit: (property: Property) => void;
}

const AdminPropertyCard = ({ property, onEdit }: AdminPropertyCardProps) => {
  const { deleteProperty } = usePropertyMutations();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteProperty.mutateAsync(property.id);
    } finally {
      setIsDeleting(false);
    }
  };

  // Format location from neighborhood data
  const locationDisplay = property.neighborhood ? `${property.neighborhood.city}, ${property.neighborhood.state}` : '';

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg line-clamp-2">{property.title}</CardTitle>
        <div className="flex items-center justify-end space-x-1">
          {property.featured && (
            <Badge variant="secondary" className="text-xs">
              Destacada
            </Badge>
          )}
          <Badge variant={property.available ? "default" : "destructive"} className="text-xs">
            {property.available ? "Disponible" : "No disponible"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pb-3">
        {property.images && property.images.length > 0 && (
          <div className="mb-3">
            <img 
              src={property.images[0]} 
              alt={property.title}
              className="w-full h-32 object-cover rounded-md"
            />
          </div>
        )}
        
        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
          {property.shortDescription}
        </p>
        
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="font-medium">Precio:</span> ${property.price.toLocaleString()} MXN/noche
          </div>
          <div>
            <span className="font-medium">Huéspedes:</span> {property.guests}
          </div>
          <div>
            <span className="font-medium">Habitaciones:</span> {property.bedrooms}
          </div>
          <div>
            <span className="font-medium">Baños:</span> {property.bathrooms}
          </div>
        </div>
        
        <div className="mt-2">
          <span className="font-medium text-sm">Ubicación:</span>
          <p className="text-sm text-gray-600">{locationDisplay}</p>
        </div>
      </CardContent>

      <CardFooter className="pt-3 border-t">
        <div className="flex space-x-2 w-full">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onEdit(property)}
            className="flex-1"
          >
            <Edit className="h-4 w-4 mr-1" />
            Editar
          </Button>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="destructive" 
                size="sm" 
                className="flex-1"
              >
                <Trash className="h-4 w-4 mr-1" />
                Eliminar
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. Esto eliminará permanentemente la propiedad
                  "{property.title}" y todos sus datos asociados.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {isDeleting ? 'Eliminando...' : 'Eliminar'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardFooter>
    </Card>
  );
};

export default AdminPropertyCard;
